import { create } from 'zustand';
import merge from 'lodash/merge';
import type { LoanRequestBodyType } from '@repo/api/endpoints/loan';
import {
  LinkedDocument,
  LoanId,
  Money,
  Period,
  SmsId,
  Limit,
  LimitMerchant,
  OfferLimit,
  LoanStage,
  ProviderType,
} from '@repo/shared/types';

import type { DiscountAppliedData } from '@/components/promotion-code/promotion-code-storage';
import { RequestedLoanValues } from '@/components/loan-offer-data-process/form/form.schema';

export type DiscountAppliedCallback = (data: DiscountAppliedData, code: string) => void;

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export const LOAN_PROCESS_STATE = {
  APPLICATION_POSITIVE: 'APPLICATION_POSITIVE',
  APPLICATION_CHANGED_LOAN_AMOUNT: 'APPLICATION_CHANGED_LOAN_AMOUNT',
  APPLICATION_PROCESSING: 'APPLICATION_PROCESSING',
  APPLICATION_NEGATIVE: 'APPLICATION_NEGATIVE',
  APPLICATION_SUSPENDED: 'APPLICATION_SUSPENDED',
  AGREEMENT_AND_APPROVALS: 'AGREEMENT_AND_APPROVALS',
  AGREEMENT_SMS_CONFIRMATION: 'AGREEMENT_SMS_CONFIRMATION',
  AGREEMENT_LIBERIS_IFRAME: 'AGREEMENT_LIBERIS_IFRAME',
  AGREEMENT_SUCCESSFUL_SIGNED: 'AGREEMENT_SUCCESSFUL_SIGNED',
  AGREEMENT_SMS_TOO_MANY_ATTEMPTS: 'AGREEMENT_SMS_TOO_MANY_ATTEMPTS',
  AGREEMENT_AND_APPROVALS_PROCESSING: 'AGREEMENT_AND_APPROVALS_PROCESSING',
} as const;

export type LoanModalProcessUnion = (typeof LOAN_PROCESS_STATE)[keyof typeof LOAN_PROCESS_STATE];

export type LoanProcessStateKeys = keyof typeof LOAN_PROCESS_STATE;

export interface AllViewsOptions {
  stageViewType?: LoanModalProcessUnion;
  [LOAN_PROCESS_STATE.AGREEMENT_AND_APPROVALS]: {
    openInEditMode: boolean;
  };
  [LOAN_PROCESS_STATE.AGREEMENT_AND_APPROVALS_PROCESSING]: {
    darkMode: boolean;
  };
}

const viewOptionsInitialState: AllViewsOptions = {
  AGREEMENT_AND_APPROVALS: {
    openInEditMode: false,
  },
  AGREEMENT_AND_APPROVALS_PROCESSING: {
    darkMode: false,
  },
};

export interface LoanStoreData {
  amount: Money;
  id: LoanId;
  period: Period;
  pricing: Money;
  loanRepaymentPrice: Money;
  documents: LinkedDocument[];
  loanAmountChange: boolean;
  provider: ProviderType;
}

interface LoanProcessState {
  currentStage?: LoanStage;
  loanProcessState?: LoanModalProcessUnion;
  applicationFormData?: LoanRequestBodyType;
  viewOptions: AllViewsOptions;
  loanData?: LoanStoreData;
  loanRejectionDescription?: string[];
  howDoYouKnowAboutP24FinancingReason?: string;
  loanLimit?: Limit | LimitMerchant | OfferLimit;
  loanSMSIds: SmsId[];
  requestedLoanValues: RequestedLoanValues | undefined;
  onDiscountApplied?: DiscountAppliedCallback;
  onDiscountCleared?: () => void;
}

interface LoanProcessActions {
  setCurrentStage: (
    currentStage: LoanStage,
    data?: {
      loanProcessState?: LoanModalProcessUnion;
      applicationFormData?: LoanRequestBodyType;
      viewOptions?: Partial<AllViewsOptions>;
    },
  ) => void;
  setLoanData: (loanData: LoanStoreData) => void;
  setLoanRejectionDescription: (loanRejectionDescription: string[]) => void;
  setHowDoYouKnowAboutP24Financing: (howDoYouKnowAboutP24FinancingReason: string) => void;
  setLoanLimit: (loanLimit: Limit | LimitMerchant | OfferLimit) => void;
  setLoanProcessState: (loanModalState?: LoanModalProcessUnion) => void;
  setLoanSMSIds: (smsId: SmsId[]) => void;
  pushLoanSMSId: (smsId: SmsId) => void;
  clearLoanProcessStore: () => void;
  setRequestedLoanValues: (requestedLoanValues: DeepPartial<RequestedLoanValues>) => void;
  setOnDiscountApplied: (
    cb: ((data: DiscountAppliedData, code: string) => void) | undefined,
  ) => void;
  setOnDiscountCleared: (cb: (() => void) | undefined) => void;
}

interface LoanProcessStore {
  state: LoanProcessState;
  actions: LoanProcessActions;
}
export const useLoanProcessStore = create<LoanProcessStore>((set) => ({
  state: {
    currentStage: undefined,
    applicationFormData: undefined,
    loanProcessState: undefined,
    viewOptions: viewOptionsInitialState,
    loanData: undefined,
    loanRejectionDescription: undefined,
    howDoYouKnowAboutP24FinancingReason: undefined,
    loanLimit: undefined,
    loanSMSIds: [],
    requestedLoanValues: undefined,
    onDiscountApplied: undefined,
    onDiscountCleared: undefined,
  },
  actions: {
    setCurrentStage: (currentStage, data) => {
      set(({ state }) => ({
        state: {
          ...state,
          currentStage,
          ...data,
          viewOptions: merge(state.viewOptions, data?.viewOptions),
        },
      }));
    },
    setLoanProcessState: (loanProcessState) => {
      set(({ state }) => ({
        state: {
          ...state,
          loanProcessState,
        },
      }));
    },
    setLoanData: (loanData) => {
      set(({ state }) => ({
        state: {
          ...state,
          loanData,
        },
      }));
    },
    setLoanRejectionDescription: (loanRejectionDescription) => {
      set(({ state }) => ({
        state: {
          ...state,
          loanRejectionDescription,
        },
      }));
    },
    setHowDoYouKnowAboutP24Financing: (howDoYouKnowAboutP24FinancingReason) => {
      set(({ state }) => ({
        state: {
          ...state,
          howDoYouKnowAboutP24FinancingReason,
        },
      }));
    },
    setLoanLimit: (loanLimit) => {
      set(({ state }) => ({
        state: {
          ...state,
          loanLimit,
        },
      }));
    },
    setLoanSMSIds: (smsIds) => {
      set(({ state }) => ({
        state: {
          ...state,
          loanSMSIds: smsIds,
        },
      }));
    },
    pushLoanSMSId: (smsId) => {
      set(({ state }) => ({
        state: {
          ...state,
          loanSMSIds: [...state.loanSMSIds, smsId],
        },
      }));
    },
    setRequestedLoanValues: (requestedLoanValues) => {
      set(({ state }) => ({
        state: {
          ...state,
          requestedLoanValues: merge({}, state.requestedLoanValues, requestedLoanValues),
        },
      }));
    },
    clearLoanProcessStore: () => {
      set(({ state }) => ({
        state: {
          currentStage: undefined,
          applicationFormData: undefined,
          currentLoan: undefined,
          loanProcessState: undefined,
          viewOptions: viewOptionsInitialState,
          loanData: undefined,
          loanLimit: undefined,
          loanSMSIds: [],
          howDoYouKnowAboutP24FinancingReason: undefined,
          loanRejectionReason: undefined,
          requestedLoanValues: undefined,
          onDiscountApplied: state.onDiscountApplied,
          onDiscountCleared: state.onDiscountCleared,
        },
      }));
    },
    setOnDiscountApplied: (cb) => {
      set(({ state }) => ({
        state: {
          ...state,
          onDiscountApplied: cb,
        },
      }));
    },
    setOnDiscountCleared: (cb) => {
      set(({ state }) => ({
        state: {
          ...state,
          onDiscountCleared: cb,
        },
      }));
    },
  },
}));
